import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listGroupConfigurationsByClassId } from "../../api/groupConfigurationApi";
import { listExamConfigs, listSubjectConfigs, getExamMarkGrid, deleteExamMarks } from "../../api/examApi";
import "../Attendance/Attendance.css";

const subjectApi = createLookupApi("subjects");
const academicYearApi = createLookupApi("academic-years");

/**
 * Direct port of digicampusbd's `exam-mark-delete` ("Mark Delete") — a
 * dedicated year/section/exam/subject-scoped grid of already-entered marks
 * with a per-row checkbox (plus "select all") and a single bulk Delete
 * action, distinct from the already-built `ExamMarkEntryPage`'s inline
 * per-row delete button. Confirmed via the old app's real template
 * (`exam-mark-delete.html`) that this reuses the exact same student-mark
 * grid fetch as Mark Input (`/exam/mark/update/student/list`, ported here
 * as `getExamMarkGrid`) and the same delete endpoint (`deleteExamMarks`) —
 * this page itself was a pure frontend gap: the backend
 * (`ExamMarkController.deleteMarks`) already existed, but no page/route
 * ever reached it.
 *
 * Carries the same real "Select Group" filter as `ExamMarkEntryPage` (see
 * that page's javadoc) — this page's own doc previously said the old app's
 * Group dropdown "only narrows the subject list" and was skippable; that was
 * wrong (found while wiring the group/subject-status scoping into
 * `getExamMarkGrid` itself — the old app's `exam-mark-delete.html` posts
 * `groupId` to the exact same `@NotNull`-grouped search as Mark Input).
 * Fixed alongside that change since `getExamMarkGrid` now requires it.
 */
export default function ExamMarkDeletePage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [subjectConfigs, setSubjectConfigs] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [subjectNames, setSubjectNames] = useState({});
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    Promise.all([listClassConfigurations(), subjectApi.list(), academicYearApi.list()]).then(([configs, subjects, years]) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
      const map = {};
      subjects.forEach((s) => (map[s.id] = s.name));
      setSubjectNames(map);
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
    listGroupConfigurationsByClassId(cc.classId).then((list) => {
      setGroups(list);
      setGroupId(list.length > 0 ? String(list[0].groupId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!examConfigId) {
      setSubjectConfigs([]);
      return;
    }
    listSubjectConfigs(examConfigId).then((list) => {
      setSubjectConfigs(list);
      setSubjectId(list.length > 0 ? String(list[0].subjectId) : "");
    });
  }, [examConfigId]);

  const handleSearch = () => {
    if (!examConfigId || !subjectId || !classConfigurationId || !groupId || !academicYear) {
      setError("Please select a year, section, exam, group, and subject.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    setSelectedIds([]);
    getExamMarkGrid(examConfigId, subjectId, classConfigurationId, groupId, academicYear)
      .then((data) => setRows(data.filter((r) => r.markInputId)))
      .catch(() => setError("Failed to load mark grid."))
      .finally(() => setLoading(false));
  };

  const toggleRow = (markInputId) =>
    setSelectedIds((prev) => (prev.includes(markInputId) ? prev.filter((id) => id !== markInputId) : [...prev, markInputId]));

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : rows.map((r) => r.markInputId));

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      setError("Please select at least one row to delete.");
      return;
    }
    if (!window.confirm(`Delete marks for ${selectedIds.length} selected student(s)? This cannot be undone.`)) return;
    setDeleting(true);
    setError("");
    try {
      await deleteExamMarks(selectedIds);
      setSuccess("Mark successfully deleted");
      handleSearch();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete marks.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Mark Delete</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Select Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Section
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {groups.length === 0 && <option value="">No groups configured</option>}
            {groups.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.groupName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Subject
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
            {subjectConfigs.length === 0 && <option value="">No subjects configured</option>}
            {subjectConfigs.map((sc) => (
              <option key={sc.subjectId} value={sc.subjectId}>
                {subjectNames[sc.subjectId] || sc.subjectName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={loading}>
          {loading ? "Searching..." : "Search"}
        </button>
      </div>

      {rows.length > 0 && (
        <>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  </th>
                  <th>Student</th>
                  <th>Student ID</th>
                  <th>Roll</th>
                  <th>Total Marks</th>
                  <th>Grade</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.markInputId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.markInputId)}
                        onChange={() => toggleRow(row.markInputId)}
                      />
                    </td>
                    <td>{row.studentName}</td>
                    <td>{row.customStudentId}</td>
                    <td>{row.studentRoll}</td>
                    <td>
                      {row.totalScore}/{row.totalMark}
                    </td>
                    <td>{row.letterGrade}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleDelete} disabled={deleting || selectedIds.length === 0}>
              {deleting ? "Deleting..." : `Delete (${selectedIds.length} selected)`}
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
